import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  GridItem,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Badge,
  Textarea,
  Checkbox,
  Radio,
  Stack,
  Flex,
  Progress,
  Input,
  Tooltip,
  useBreakpointValue,
} from "@chakra-ui/react";
import {
  TimeIcon,
  WarningIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  InfoIcon,
  QuestionOutlineIcon,
} from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import CodeEditor from "../../components/CodeEditor/CodeEditor";
import ReportQuestionModal from "../../components/Modal/ReportQuestionModal";
import { useCopyPastePrevention } from "../../hooks/useCopyPastePrevention";
import { useStreamingSTT } from "../../hooks/useStreamingSTT";
import axios from "axios";
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";

// Human-readable labels for question types (shown in question header), keyed by language (EN/FR)
const QUESTION_TYPE_LABELS = {
  en: {
    OPEN: "Open question",
    CODING: "Coding question",
    CODE_COMPLETION: "Code completion",
    KATA_OPEN: "Kata open",
    KATA_CODING: "Kata coding",
    QCM: "Single choice",
    "MULTI-CHOICE": "Multiple choice",
  },
  fr: {
    OPEN: "Question ouverte",
    CODING: "Question de coding",
    CODE_COMPLETION: "Complétion de code",
    KATA_OPEN: "Kata ouvert",
    KATA_CODING: "Kata coding",
    QCM: "Choix unique",
    "MULTI-CHOICE": "Choix multiples",
  },
};

// Section labels for layout indicators, keyed by test context language (EN/FR)
const SECTION_LABELS = {
  en: {
    context: "Question Context",
    mainQuestion: "Main Question",
    parameters: "Parameters",
    scoringRules: "Scoring Rules",
    question: "Question",
    singleAnswerExpected: "Single answer expected.",
    multipleAnswersExpected: "Multiple answers expected.",
    micHint: "Speak in short sentences and pause briefly between each one. The microphone transcribes as you go — avoid long uninterrupted sentences.",
  },
  fr: {
    context: "Contexte de la question",
    mainQuestion: "Question principale",
    parameters: "Paramètres",
    scoringRules: "Règles de notation",
    question: "Question",
    singleAnswerExpected: "Une seule réponse attendue.",
    multipleAnswersExpected: "Réponses multiples attendues.",
    micHint: "Parlez en phrases courtes et faites une pause entre chacune. Le microphone transcrit au fur et à mesure — évitez les longues phrases sans interruption.",
  },
};

const QuestionPage = () => {
  const { uuid, qid } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const questionId = parseInt(qid, 10);

  const [isSoundEnabled, setIsSoundEnabled] = useState(true);
  const chimePlayedRef = useRef(false);
  const alertPlayedRef = useRef(false);
  const chimeAudioRef = useRef(null);
  const alertAudioRef = useRef(null);

  const isDesktop = useBreakpointValue({ base: false, lg: true }, { fallback: false });
  
  const [questions, setQuestions] = useState([]);
  const [maxQuestionReached, setMaxQuestionReached] = useState(() => {
    const stored = localStorage.getItem(`furthestQuestion_${uuid}`);
    const parsed = stored ? parseInt(stored, 10) : NaN;
    const fallback = Number.isFinite(questionId) ? questionId : 1;
    return Number.isFinite(parsed) ? parsed : fallback;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [textAnswer, setTextAnswer] = useState('');
  const [timeLeft, setTimeLeft] = useState(0);
  const [questionStartTime, setQuestionStartTime] = useState(() => {
    // Try to get stored question start time from localStorage
    const stored = localStorage.getItem(`questionStartTime_${uuid}_${questionId}`);
    return stored ? parseInt(stored, 10) : null;
  });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [previewResult, setPreviewResult] = useState(null);
  // Modal to report question
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  // Streaming STT (speech-to-text)
  const { isRecording, isConnecting, interimTranscript, error: sttError, start: startSTT, stop: stopSTT } = useStreamingSTT(uuid);
  const [sttCommitted, setSttCommitted] = useState([]); // array of finalized segments
  const [sttBaseText, setSttBaseText] = useState(""); // snapshot for rendering
  const sttBaseRef = useRef(""); // immediate snapshot for finalization logic
  const lastSttFinalRef = useRef("");
  const sttSessionRef = useRef(0); // Track STT sessions to detect issues
  const sttQuestionRef = useRef(null); // question number tied to the current STT session

  // Track how many times user left the page (optional)
  const [violations, setViolations] = useState(0);

  const getGroupKey = useCallback((q) => q?.technology ?? "", []);

  const isOpenQuestionType = useCallback(
    (type) => type === "OPEN" || type === "KATA_OPEN",
    []
  );
  const isCodingQuestionType = useCallback(
    (type) => type === "CODING" || type === "KATA_CODING",
    []
  );
  const isTypedResponseQuestionType = useCallback(
    (type) => isOpenQuestionType(type) || isCodingQuestionType(type) || type === "CODE_COMPLETION",
    [isOpenQuestionType, isCodingQuestionType]
  );

  // Seeded shuffle function for consistent shuffling based on question ID
  const seededShuffle = useCallback((array, seed) => {
    // Create a copy to avoid mutating original
    const shuffled = [...array];
    
    // Simple seeded random number generator
    const seededRandom = (seed) => {
      const x = Math.sin(seed) * 10000;
      return x - Math.floor(x);
    };
    
    // Fisher-Yates shuffle with seeded random
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(seededRandom(seed + i) * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    
    return shuffled;
  }, []);

  // Helper to generate MCQ options from current question
  const generateOptions = useCallback(() => {
    if (!currentQuestion) return [];
    const options = [];
    for (let i = 1; i <= 5; i++) {
      const answerKey = `answer${i}`;
      const answerValue = currentQuestion[answerKey];
      
      // More robust check for valid answer values
      if (answerValue !== null && 
          answerValue !== undefined && 
          answerValue !== '' && 
          answerValue.trim() !== '' &&
          answerValue.length > 0) {
        options.push({
          id: `option${i}`,
          text: answerValue.trim(), // Ensure clean text
          originalIndex: i // Store original index for answer mapping
        });
      }
    }
    
    // Shuffle options using question ID as seed for consistency
    // This ensures the same question always has the same shuffle order
    const seed = currentQuestion.questionId || currentQuestion.questionNumber;
    const shuffledOptions = seededShuffle(options, seed);
    
    return shuffledOptions;
  }, [currentQuestion, seededShuffle]);

  // Helper: send a violation event to the backend
  const reportViolation = useCallback(async () => {
    try {
      // Increment local counter
      setViolations((v) => v + 1);

      await axios.post(
        `${process.env.REACT_APP_BASE_API_URL}/api/evaluate/${uuid}/violation`,
        {
          occurred_at: new Date().toISOString(),
          count: violations + 1,
          question_id: currentQuestion ? currentQuestion.questionId : null,
        }
      );
    } catch (err) {
      console.error('Error reporting violation:', err);
    }
  }, [uuid, violations, currentQuestion]);

  // Detect when the tab/window loses focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        reportViolation();
      }
    };

    const handleWindowBlur = () => {
      reportViolation();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, [uuid, violations, reportViolation]);

  // Prevent copy-paste functionality during evaluation
  useCopyPastePrevention();

  // Helper to auto-save an MCQ answer (QCM / MULTI-CHOICE) when time is up
  const autoSaveMcqAnswer = useCallback(() => {
    if (!currentQuestion) return;
    if (currentQuestion.questionType !== 'QCM' && currentQuestion.questionType !== 'MULTI-CHOICE') return;

    let answerValue = '';

    if (currentQuestion.questionType === 'MULTI-CHOICE') {
      const textValues = selectedOptions.map(id => {
        const optionNum = id.replace('option', '');
        return currentQuestion[`answer${optionNum}`];
      }).filter(Boolean);
      answerValue = textValues.join(', ');
    } else if (currentQuestion.questionType === 'QCM') {
      const optionNum = selectedOptions[0]?.replace('option', '');
      answerValue = optionNum ? currentQuestion[`answer${optionNum}`] : '';
    }

    // Save even if empty to mark unanswered
    setAnswers(prev => {
      const newAnswers = { ...prev, [currentQuestion.questionNumber]: answerValue };
      localStorage.setItem(`answers_${uuid}`, JSON.stringify(newAnswers));
      return newAnswers;
    });
  }, [currentQuestion, selectedOptions, uuid]);
  const [answers, setAnswers] = useState(() => {
    // Try to get stored answers from localStorage
    const storedAnswers = localStorage.getItem(`answers_${uuid}`);
    return storedAnswers ? JSON.parse(storedAnswers) : {};
  });

  // Handle finalized STT segments: push into committed, persist combined (base + committed)
  const handleSttFinal = useCallback((finalText, sourceQuestionNumberParam) => {
    const cleaned = (finalText || "").trim();
    if (!cleaned) return;
    // Determine the question this STT result belongs to.
    // Prefer the explicit param; otherwise, use the session ref; never fall back to currentQuestion.
    const candidate = Number.isFinite(sourceQuestionNumberParam)
      ? sourceQuestionNumberParam
      : (Number.isFinite(sttQuestionRef.current) ? sttQuestionRef.current : undefined);
    const sourceQuestionNumber = candidate;
    if (!Number.isFinite(sourceQuestionNumber)) return; // Safety check
    
    // Debug logging for STT issues (can be removed in production)
    if (process.env.NODE_ENV === 'development') {
      console.log('STT Final received:', {
        text: cleaned,
        previous: lastSttFinalRef.current,
        questionNumber: sourceQuestionNumber,
        timestamp: new Date().toISOString()
      });
    }
    
    // More lenient deduplication - only skip if the exact same text was just processed
    // This allows for legitimate repeated phrases while preventing true duplicates
    if (lastSttFinalRef.current === cleaned) return;
    lastSttFinalRef.current = cleaned;
    
    // If this STT result is not for the currently displayed question, persist it only to answers/localStorage
    // and do not touch UI state of the current question.
    if (!currentQuestion || currentQuestion.questionNumber !== sourceQuestionNumber) {
      setAnswers((prevAnswers) => {
        const previousText = (prevAnswers[sourceQuestionNumber] || "").trim();
        const updatedText = previousText ? `${previousText} ${cleaned}` : cleaned;
        const updated = { ...prevAnswers, [sourceQuestionNumber]: updatedText };
        localStorage.setItem(`answers_${uuid}`, JSON.stringify(updated));
        return updated;
      });
      return;
    }
    
    // Capture question number in closure to prevent race conditions
    const questionNumber = sourceQuestionNumber;
    
    // Use functional updates to ensure we get the latest state
    setSttCommitted((prev) => {
      const next = [...prev, cleaned];
      const committedJoined = next.join(" ");
      
      // Get the current textAnswer state to ensure we're building on the latest text
      setTextAnswer((currentTextAnswer) => {
        const base = (currentTextAnswer || "").trim();
        const combined = base ? `${base} ${committedJoined}` : committedJoined;
        
        // Debug logging for text combination
        if (process.env.NODE_ENV === 'development') {
          console.log('STT Text combination:', {
            base: base,
            newSegment: cleaned,
            committed: committedJoined,
            combined: combined
          });
        }
        
        // Safeguard: Ensure we're not overwriting existing text inappropriately
        // If the combined text is shorter than the base, something went wrong
        if (base && combined.length < base.length) {
          console.warn('STT: Potential text overwrite detected', {
            base: base,
            combined: combined,
            newSegment: cleaned
          });
          // In this case, keep the base text and just append the new segment
          const safeCombined = `${base} ${cleaned}`;
          return safeCombined;
        }
        
        // Update answers with the combined text
        setAnswers((prevAnswers) => {
          const updated = { ...prevAnswers, [questionNumber]: combined };
          localStorage.setItem(`answers_${uuid}`, JSON.stringify(updated));
          return updated;
        });
        
        return combined;
      });
      
      return next;
    });
  }, [currentQuestion, uuid]);

  // Fetch questions only once per evaluation. Order is defined by the backend (no client-side sorting).
  // Uses navigateRef to avoid re-fetching when navigate's identity changes on route param updates.
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_API_URL}/api/evaluate/${uuid}/questions`
        );
        setQuestions(response.data || []);

        if (!localStorage.getItem(`startTime_${uuid}`)) {
          localStorage.setItem(`startTime_${uuid}`, new Date().toISOString());
        }

        setLoading(false);
      } catch (err) {
        if (err?.response?.status === 409) {
          navigateRef.current(`/evaluate/${uuid}/welcome`, { replace: true });
          return;
        }
        console.error('Error fetching questions:', err);
        setError('Failed to load questions. Please try again later.');
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [uuid]);

  // Stop STT when navigating to a different question
  useEffect(() => {
    if (isRecording) {
      const t = (interimTranscript || '').trim();
      if (t) handleSttFinal(t, sttQuestionRef.current ?? currentQuestion?.questionNumber);
      stopSTT();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionId]);

  // Keyboard shortcut to stop recording (Esc) while recording
  useEffect(() => {
    const onKeyDown = (e) => {
      if (!isRecording) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        const t = (interimTranscript || '').trim();
        if (t) handleSttFinal(t, sttQuestionRef.current ?? currentQuestion?.questionNumber);
        stopSTT();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isRecording, stopSTT, interimTranscript, handleSttFinal]);

  // Fallback: if the stream ends remotely and leaves an interim, commit it
  useEffect(() => {
    if (!isRecording) {
      const t = (interimTranscript || '').trim();
      if (t) handleSttFinal(t, sttQuestionRef.current);
      // Clear association after committing fallback
      sttQuestionRef.current = null;
    }
  }, [isRecording, interimTranscript, handleSttFinal]);

  // Set current question when questions are loaded or qid changes
  useEffect(() => {
    if (questions.length > 0) {
      const question = questions.find(q => q.questionNumber === questionId);
      if (question) {
        setCurrentQuestion(question);
        
        // Calculate time left based on when question was first viewed
        const storedStartTime = localStorage.getItem(`questionStartTime_${uuid}_${questionId}`);
        const now = Date.now();
        
        if (storedStartTime) {
          // Question was viewed before - calculate remaining time
          const startTime = parseInt(storedStartTime, 10);
          const elapsedSeconds = Math.floor((now - startTime) / 1000);
          const remainingTime = Math.max(0, question.responseTime - elapsedSeconds);
          
          if (remainingTime === 0) {
            // Time has already expired - remove the stored start time
            localStorage.removeItem(`questionStartTime_${uuid}_${questionId}`);
            setTimeLeft(0);
            setQuestionStartTime(null);
            
            // Auto-advance: next is by array position (backend order)
            setTimeout(() => {
              const idx = questions.findIndex((q) => q.questionNumber === question.questionNumber);
              const nextQuestion = idx >= 0 && idx < questions.length - 1 ? questions[idx + 1] : null;
              if (!nextQuestion) {
                handleSubmit();
              } else {
                const nextQuestionNumber = nextQuestion.questionNumber;
                const currentGroupKey = getGroupKey(question);
                const nextGroupKey = getGroupKey(nextQuestion);

                if (nextGroupKey !== currentGroupKey) {
                  markGroupCompleted(question);
                  setMaxQuestionReached((prev) => {
                    const newMax = prev ? Math.max(prev, nextQuestionNumber) : nextQuestionNumber;
                    localStorage.setItem(`furthestQuestion_${uuid}`, newMax);
                    return newMax;
                  });
                  navigate(`/evaluate/${uuid}/summary`);
                } else {
                  setMaxQuestionReached((prev) => {
                    const newMax = prev ? Math.max(prev, nextQuestionNumber) : nextQuestionNumber;
                    localStorage.setItem(`furthestQuestion_${uuid}`, newMax);
                    return newMax;
                  });
                  navigate(`/evaluate/${uuid}/q/${nextQuestionNumber}`);
                }
              }
            }, 100);
          } else {
            setTimeLeft(remainingTime);
            setQuestionStartTime(startTime);
          }
        } else {
          // First time viewing this question - start fresh timer
          const startTime = now;
          localStorage.setItem(`questionStartTime_${uuid}_${questionId}`, startTime.toString());
          setTimeLeft(question.responseTime);
          setQuestionStartTime(startTime);
        }

        // CRITICAL: Clear textAnswer immediately to prevent showing previous question's answer
        setTextAnswer('');
        setSelectedOptions([]);
        setPreviewError("");
        setPreviewResult(null);

        // Reset STT buffers on question change
        setSttCommitted([]);
        setSttBaseText("");
        sttBaseRef.current = "";
        lastSttFinalRef.current = "";
        sttQuestionRef.current = null;

        // Reset audio cue flags for the new question
        chimePlayedRef.current = false;
        alertPlayedRef.current = false;

        setMaxQuestionReached((prevMax) => {
          const newMax = prevMax ? Math.max(prevMax, question.questionNumber) : question.questionNumber;
          if (newMax !== prevMax) {
            localStorage.setItem(`furthestQuestion_${uuid}`, newMax);
          }
          return newMax;
        });
      } else if (questionId > questions.length) {
        // If on a non-existent question (beyond the last one), navigate to summary/submit page
        navigate(`/evaluate/${uuid}/submit`);
      }
    }
  }, [questions, questionId, uuid, navigate]);

  useEffect(() => {
    if (!Number.isFinite(questionId) || maxQuestionReached === null) return;
    if (questionId < maxQuestionReached) {
      navigate(`/evaluate/${uuid}/q/${maxQuestionReached}`, { replace: true });
    }
  }, [questionId, maxQuestionReached, navigate, uuid]);

  // ---------------------------------------------------------------------------
  // GROUPING HELPERS
  const markGroupCompleted = useCallback((question) => {
    if (!question) return;
    const groupKey = getGroupKey(question);
    const storageKey = `completedGroups_${uuid}`;
    let completed = [];
    try {
      completed = JSON.parse(localStorage.getItem(storageKey)) || [];
    } catch {
      completed = [];
    }
    if (!completed.includes(groupKey)) {
      completed.push(groupKey);
      localStorage.setItem(storageKey, JSON.stringify(completed));
    }
    localStorage.removeItem(`currentQuestion_${uuid}`);
  }, [uuid]);

  // Build full question text for submission so scoring criteria is always included (OPEN/CODING).
  const getQuestionTextForSubmission = useCallback((question) => {
    if (!question) return '';
    const isStructured =
      question.questionType === 'OPEN' || question.questionType === 'CODING' ||
      question.questionType === 'KATA_OPEN' || question.questionType === 'KATA_CODING';
    if (!isStructured) return (question.questionText || '').trim();

    const trim = (v) => (typeof v === 'string' ? v.trim() : '');
    const context = trim(question.questionContext);
    const main = trim(question.questionMain) || trim(question.questionText);
    const params = Array.isArray(question.questionParameters)
      ? question.questionParameters.map((p) => trim(p)).filter(Boolean)
      : [];
    const scoring = trim(question.questionScoringFooter);

    const hasStructured = context || main || params.length > 0 || scoring;
    if (!hasStructured) return (question.questionText || '').trim();

    const parts = [];
    if (context) parts.push(context);
    if (main) parts.push(main);
    if (params.length > 0) parts.push(params.map((p) => `- ${p}`).join('\n'));
    if (scoring) parts.push(scoring);
    return parts.join('\n\n');
  }, []);
  // ---------------------------------------------------------------------------

  // Timer effect - modified to not submit answers
  useEffect(() => {
    if (!currentQuestion || loading) return;

    // Capture the current question number in a stable reference
    const currentQuestionNumber = currentQuestion.questionNumber;

    const timer = setInterval(() => {
      setTimeLeft(prevTime => {
        if (prevTime <= 1) {
          clearInterval(timer);

          // Remove timer start time since time has expired
          localStorage.removeItem(`questionStartTime_${uuid}_${questionId}`);

          // Auto-save MCQ answer before leaving
          autoSaveMcqAnswer();

          // Save OPEN/CODING/CODE_COMPLETION answer and ensure STT is stopped when time is up
          try {
            if (currentQuestion && isTypedResponseQuestionType(currentQuestion.questionType)) {
              const t = (interimTranscript || '').trim();
              if (t) handleSttFinal(t, currentQuestionNumber);
              stopSTT();
              // Ensure the current answer is persisted before navigation using stable question number
              setAnswers((prev) => {
                const updated = { ...prev, [currentQuestionNumber]: textAnswer || '' };
                localStorage.setItem(`answers_${uuid}`, JSON.stringify(updated));
                return updated;
              });
            }
          } catch {}

          const idx = questions.findIndex((q) => q.questionNumber === currentQuestion.questionNumber);
          const nextQuestion = idx >= 0 && idx < questions.length - 1 ? questions[idx + 1] : null;
          if (!nextQuestion) {
            markGroupCompleted(currentQuestion);
            setTimeout(() => handleSubmit(), 0);
          } else {
            const nextQuestionNumber = nextQuestion.questionNumber;
            const currentGroupKey = getGroupKey(currentQuestion);
            const nextGroupKey = getGroupKey(nextQuestion);

            if (nextGroupKey !== currentGroupKey) {
              markGroupCompleted(currentQuestion);
              setMaxQuestionReached((prev) => {
                const newMax = prev ? Math.max(prev, nextQuestionNumber) : nextQuestionNumber;
                localStorage.setItem(`furthestQuestion_${uuid}`, newMax);
                return newMax;
              });
              setTimeout(() => navigate(`/evaluate/${uuid}/summary`), 0);
            } else {
              setMaxQuestionReached((prev) => {
                const newMax = prev ? Math.max(prev, nextQuestionNumber) : nextQuestionNumber;
                localStorage.setItem(`furthestQuestion_${uuid}`, newMax);
                return newMax;
              });
              setTimeout(() => navigate(`/evaluate/${uuid}/q/${nextQuestionNumber}`), 0);
            }
          }
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [currentQuestion, loading, questionId, questions, navigate, uuid, autoSaveMcqAnswer, markGroupCompleted, stopSTT]);

  // Also stop STT when timer hits zero through other means
  useEffect(() => {
    if (timeLeft <= 0 && isRecording) {
      stopSTT();
    }
  }, [timeLeft, isRecording, stopSTT]);

  // Audio cues: chime at 60s remaining, alert at 10s remaining
  useEffect(() => {
    if (!isSoundEnabled) return;
    if (timeLeft === 60 && !chimePlayedRef.current) {
      chimePlayedRef.current = true;
      try {
        if (!chimeAudioRef.current) chimeAudioRef.current = new Audio("/sounds/chime.wav");
        const p = chimeAudioRef.current.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      } catch {}
    }
    if (timeLeft === 10 && !alertPlayedRef.current) {
      alertPlayedRef.current = true;
      try {
        if (!alertAudioRef.current) alertAudioRef.current = new Audio("/sounds/alert.wav");
        const p = alertAudioRef.current.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      } catch {}
    }
  }, [timeLeft, isSoundEnabled]);

  // Load previously selected answer when question changes
  useEffect(() => {
    if (!currentQuestion) return;
    
    // Check if there's a saved answer for this specific question
    const savedAnswer = answers[currentQuestion.questionNumber];
    
    if (savedAnswer) {
      // Only restore saved answer if it exists for THIS question
      if (isTypedResponseQuestionType(currentQuestion.questionType)) {
        setTextAnswer(savedAnswer);
        setSelectedOptions([]);
      }
      else if (currentQuestion.questionType === 'QCM' || currentQuestion.questionType === 'MULTI-CHOICE') {
        const options = generateOptions();
        if (currentQuestion.questionType === 'MULTI-CHOICE') {
          // For multi-choice, restore selected option IDs
          const selectedIds = savedAnswer.split(',').map(t => t.trim());
          setSelectedOptions(selectedIds);
        } else {
          // For single choice, find the one option with matching text
          const selectedOption = options.find(opt => opt.text === savedAnswer);
          setSelectedOptions(selectedOption ? [selectedOption.id] : []);
        }
        
        setTextAnswer('');
      }
    } else {
      // No saved answer for this question - ensure clean state
      setSelectedOptions([]);
      setTextAnswer('');
    }
  }, [currentQuestion, answers, generateOptions]);

  useEffect(() => {
    // Persist the current question number so the user can resume later
    if (questionId) {
      localStorage.setItem(`currentQuestion_${uuid}`, questionId);
    }
  }, [questionId, uuid]);

  if (loading) {
    return (
      <Box minH="100vh" bg="gray.50" py={8}>
        <Container maxW="container.lg">
          <Center h="60vh">
            <VStack spacing={4}>
              <Spinner size="xl" color="purple.500" thickness="4px" />
              <Text color="gray.600">Chargement des questions...</Text>
            </VStack>
          </Center>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box minH="100vh" bg="gray.50" py={8}>
        <Container maxW="container.md">
          <Center h="60vh">
            <Alert
              status="error"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              height="200px"
              borderRadius="lg"
            >
              <AlertIcon boxSize="40px" mr={0} />
              <Heading size="md" mt={4} mb={2}>
                Une erreur est survenue
              </Heading>
              <Text mb={4}>{error}</Text>
              <Button colorScheme="red" onClick={() => window.location.reload()}>
                Réessayer
              </Button>
            </Alert>
          </Center>
        </Container>
      </Box>
    );
  }

  if (!currentQuestion) {
    return (
      <Box minH="100vh" bg="gray.50" py={8}>
        <Container maxW="container.md">
          <Center h="60vh">
            <Alert
              status="warning"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              height="200px"
              borderRadius="lg"
            >
              <AlertIcon boxSize="40px" mr={0} />
              <Heading size="md" mt={4} mb={2}>
                Question non trouvée
              </Heading>
              <Text mb={4}>La question demandée n'existe pas.</Text>
              <Button 
                bg="rgb(37, 87, 167)"
                color="white"
                _hover={{ bg: "rgb(27, 67, 137)" }}
                onClick={() => navigate(`/evaluate/${uuid}/q/1`)}
              >
                Retour à la première question
              </Button>
            </Alert>
          </Center>
        </Container>
      </Box>
    );
  }

  const handleOptionToggle = (optionId) => {
    let newSelectedOptions;

    if (currentQuestion.questionType === 'MULTI-CHOICE') {
      // For MULTI-CHOICE: toggle the option correctly
      newSelectedOptions = selectedOptions.includes(optionId)
        ? selectedOptions.filter(o => o !== optionId)
        : [...selectedOptions, optionId];
    } else if (currentQuestion.questionType === 'QCM') {
      // For QCM: just select the one option
      newSelectedOptions = [optionId];
    }
    // Update UI state
    setSelectedOptions(newSelectedOptions);

    // Generate the answer value to save (IDs for MULTI-CHOICE, text for QCM)
    let answerValue;
    if (currentQuestion.questionType === 'MULTI-CHOICE') {
      // Save selected option IDs as a comma-separated string
      answerValue = newSelectedOptions.join(',');
    } else if (currentQuestion.questionType === 'QCM') {
      // Save the answer text for QCM
      const optionNum = newSelectedOptions[0]?.replace('option', '');
      answerValue = optionNum ? currentQuestion[`answer${optionNum}`] : '';
    }
    // Important: Don't save to state/localStorage if nothing selected
    if (!answerValue) return;
    // Save a clean answer to state and localStorage
    const newAnswers = { ...answers, [currentQuestion.questionNumber]: answerValue };
    setAnswers(newAnswers);
    localStorage.setItem(`answers_${uuid}`, JSON.stringify(newAnswers));
  };

  const handleTextChange = (e) => {
    // Support both events from textarea and raw string from CodeEditor
    const text = typeof e === 'string' ? e : e.target.value;
    setTextAnswer(text);
    
    // Save answer to state
    const newAnswers = { ...answers, [currentQuestion.questionNumber]: text };
    setAnswers(newAnswers);
    
    // Save to localStorage
    localStorage.setItem(`answers_${uuid}`, JSON.stringify(newAnswers));

    if (isCodingQuestionType(currentQuestion?.questionType)) {
      setPreviewError("");
      setPreviewResult(null);
    }
  };

  const handlePreviewScoring = async () => {
    if (!currentQuestion || !isCodingQuestionType(currentQuestion.questionType)) return;

    try {
      setPreviewLoading(true);
      setPreviewError("");
      setPreviewResult(null);

      const payload = {
        user_response: textAnswer && textAnswer.trim() ? textAnswer : null,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_API_URL}/api/evaluate/${uuid}/questions/${currentQuestion.questionId}/preview`,
        payload
      );

      setPreviewResult(response.data);
    } catch (err) {
      console.error("Error previewing answer score:", err);
      setPreviewError("Unable to test this answer right now. Please try again.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleNextQuestion = () => {
    // Capture current question number to prevent race conditions
    const currentQuestionNumber = currentQuestion?.questionNumber;
    
    // Finalize and persist OPEN/CODING/CODE_COMPLETION answers, then clear textbox before navigating
    try {
      if (currentQuestion && isTypedResponseQuestionType(currentQuestion.questionType)) {
        // If dictation is active, commit any interim transcript before leaving
        if (isRecording) {
          const t = (interimTranscript || '').trim();
          if (t) handleSttFinal(t, sttQuestionRef.current ?? currentQuestionNumber);
          stopSTT();
        }
        // Ensure latest typed text is saved to answers/localStorage using stable question number
        if (currentQuestionNumber) {
          setAnswers((prev) => {
            const updated = { ...prev, [currentQuestionNumber]: textAnswer || '' };
            localStorage.setItem(`answers_${uuid}`, JSON.stringify(updated));
            return updated;
          });
        }
        // Clear textbox so the next question never shows previous answer
        setTextAnswer('');
        setSttCommitted([]);
        setSttBaseText("");
        sttBaseRef.current = "";
        lastSttFinalRef.current = "";
      }
    } catch {}

    const idx = questions.findIndex((q) => q.questionNumber === currentQuestion.questionNumber);
    const nextQuestion = idx >= 0 && idx < questions.length - 1 ? questions[idx + 1] : null;
    if (!nextQuestion) {
      markGroupCompleted(currentQuestion);
      handleSubmit();
      return;
    }
    const nextQuestionNumber = nextQuestion.questionNumber;
    const currentGroupKey = getGroupKey(currentQuestion);
    const nextGroupKey = getGroupKey(nextQuestion);

    if (nextGroupKey !== currentGroupKey) {
      markGroupCompleted(currentQuestion);
      setMaxQuestionReached((prev) => {
        const newMax = prev ? Math.max(prev, nextQuestionNumber) : nextQuestionNumber;
        localStorage.setItem(`furthestQuestion_${uuid}`, newMax);
        return newMax;
      });
      navigate(`/evaluate/${uuid}/summary`);
    } else {
      setMaxQuestionReached((prev) => {
        const newMax = prev ? Math.max(prev, nextQuestionNumber) : nextQuestionNumber;
        localStorage.setItem(`furthestQuestion_${uuid}`, newMax);
        return newMax;
      });
      navigate(`/evaluate/${uuid}/q/${nextQuestionNumber}`);
    }
  };

  // When submitting answers, convert MULTI-CHOICE IDs to text for backend
  const handleSubmit = async () => {
    try {
      markGroupCompleted(currentQuestion);
      const storedAnswers = JSON.parse(localStorage.getItem(`answers_${uuid}`));
      const startTime = localStorage.getItem(`startTime_${uuid}`);
      const payload = {
        start_at: startTime,
        completed_at: new Date().toISOString(),
        answers: questions.map(question => {
          let userAnswer = storedAnswers[question.questionNumber] || '';
          if (question.questionType === 'MULTI-CHOICE' && userAnswer) {
            // Convert IDs to text for backend
            const ids = userAnswer.split(',').map(t => t.trim());
            const texts = ids.map(id => {
              const num = id.replace('option', '');
              return question[`answer${num}`];
            }).filter(v => v !== undefined && v !== null && v !== '');
            userAnswer = texts.join(', ');
          }
          return {
            question_id: question.questionId,
            question: getQuestionTextForSubmission(question),
            user_response: userAnswer
          };
        })
      };
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_API_URL}/api/evaluate/${uuid}/response`,
        payload
      );
      if (response.status === 200) {
        localStorage.removeItem(`answers_${uuid}`);
        localStorage.removeItem(`startTime_${uuid}`);
        localStorage.removeItem(`currentQuestion_${uuid}`);
        localStorage.removeItem(`furthestQuestion_${uuid}`);
        localStorage.removeItem(`completedGroups_${uuid}`);
        
        // Clean up all question start times
        questions.forEach(q => {
          localStorage.removeItem(`questionStartTime_${uuid}_${q.questionNumber}`);
        });
        
        navigate(`/evaluate/${uuid}/success`);
      }
    } catch (error) {
      if (error?.response?.status === 409) {
        navigate(`/evaluate/${uuid}/welcome`, { replace: true });
        return;
      }
      console.error('Error submitting evaluation:', error);
      // TODO: replace with Chakra toast; browser dialogs are banned for new code
      // eslint-disable-next-line no-restricted-globals
      alert('Failed to submit evaluation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Open modal
  const handleReportQuestion = () => {
    setIsReportModalOpen(true);
  };

  // Submit the selected reason from modal
  const submitReportReason = async (reason) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_API_URL}/api/evaluate/${uuid}/questions/${currentQuestion.questionId}/report`,
        {
          question_id: currentQuestion.questionId,
          reason,
        }
      );
    } catch (err) {
      console.error('Error reporting question:', err);
      // TODO: replace with Chakra toast; browser dialogs are banned for new code
      // eslint-disable-next-line no-restricted-globals
      alert("Une erreur est survenue lors de l'envoi du signalement. Veuillez réessayer plus tard.");
    } finally {
      setIsReportModalOpen(false);
    }
  };

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Ensure the scoring rubric marker is easy to scan:
  // - starts on a new line
  // - marker label is bold in markdown
  const formatFullScoreRequirement = (text) => {
    if (!text) return "";
    // Some payloads wrap the entire scoring paragraph in **...**.
    // Remove that wrapper first so we can safely re-apply only the label styling.
    const withoutFullParagraphBold = text
      .replace(
        /\*\*(To achieve the maximum score, the response must address:[\s\S]*?)\*\*/g,
        "$1"
      )
      .replace(
        /\*\*(Pour obtenir le score maximal, la réponse doit aborder:[\s\S]*?)\*\*/g,
        "$1"
      )
      .replace(
        /\*\*(Score maximal requis:[\s\S]*?)\*\*/g,
        "$1"
      )
      .replace(
        /\*\*(Full score requires:[\s\S]*?)\*\*/g,
        "$1"
      );
    const markerRegex = /(\*\*Full score requires:\*\*|Full score requires:|\*\*Score maximal requis:\*\*|Score maximal requis:|\*\*To achieve the maximum score, the response must address:\*\*|To achieve the maximum score, the response must address:|\*\*Pour obtenir le score maximal, la réponse doit aborder:\*\*|Pour obtenir le score maximal, la réponse doit aborder:)/g;
    return withoutFullParagraphBold
      .replace(
        /([^\n])\s*(\*\*Full score requires:\*\*|Full score requires:|\*\*Score maximal requis:\*\*|Score maximal requis:|\*\*To achieve the maximum score, the response must address:\*\*|To achieve the maximum score, the response must address:|\*\*Pour obtenir le score maximal, la réponse doit aborder:\*\*|Pour obtenir le score maximal, la réponse doit aborder:)/g,
        "$1\n$2"
      )
      .replace(markerRegex, (match) =>
        match.toLowerCase().includes("pour obtenir") || match.toLowerCase().includes("score maximal")
          ? "**Pour obtenir le score maximal, la réponse doit aborder:**"
          : "**To achieve the maximum score, the response must address:**"
      );
  };

  // Normalize question text so each newline behaves like a blank line (more spacing),
  // but do not modify fenced code blocks (``` ... ```), so code snippets keep their
  // original formatting.
  const normalizeQuestionText = (text) => {
    if (!text) return "";

    const codeBlockRegex = /```[\s\S]*?```/g;
    let result = "";
    let lastIndex = 0;
    let match;

    // Walk through all fenced code blocks and only apply the newline doubling
    // to the text outside of those blocks.
    while ((match = codeBlockRegex.exec(text)) !== null) {
      const before = text.slice(lastIndex, match.index);
      if (before) {
        result += formatFullScoreRequirement(before).replace(/\n/g, "\n\n");
      }
      // Append the code block unchanged
      result += match[0];
      lastIndex = match.index + match[0].length;
    }

    // Handle any remaining text after the last code block
    const after = text.slice(lastIndex);
    if (after) {
      result += formatFullScoreRequirement(after).replace(/\n/g, "\n\n");
    }

    return result;
  };

  const options = generateOptions();
  const currentIndex = questions.findIndex((q) => q.questionNumber === currentQuestion?.questionNumber);
  const isLastQuestion = questions.length > 0 && currentIndex === questions.length - 1;
  const committedJoined = sttCommitted.join(' ');
  const liveComposed = (() => {
    const base = (sttBaseText || '').trim();
    const interim = (interimTranscript || '').trim();
    const parts = [];
    if (base) parts.push(base);
    if (committedJoined) parts.push(committedJoined);
    if (isRecording && interim) parts.push(interim);
    return parts.join(' ');
  })();
  const displayedAnswer = (isOpenQuestionType(currentQuestion?.questionType) && isRecording) ? (liveComposed || textAnswer) : textAnswer;
  const normalizeQuestionField = (value) => {
    if (typeof value !== "string") return "";
    return value.trim();
  };
  const isStructuredQuestionType =
    currentQuestion?.questionType === "OPEN" || currentQuestion?.questionType === "CODING";
  const questionTitle = normalizeQuestionField(currentQuestion?.questionTitle);
  const questionContext = normalizeQuestionField(currentQuestion?.questionContext);
  const questionMain = normalizeQuestionField(currentQuestion?.questionMain);
  const questionTextFallback = normalizeQuestionField(currentQuestion?.questionText);
  const resolvedMainQuestionText = questionMain || questionTextFallback;
  const questionParameters = Array.isArray(currentQuestion?.questionParameters)
    ? currentQuestion.questionParameters
        .map((parameter) => normalizeQuestionField(parameter))
        .filter(Boolean)
    : [];
  const questionScoringFooter = normalizeQuestionField(currentQuestion?.questionScoringFooter);
  const hasQuestionContext = Boolean(questionContext);
  const hasQuestionMain = Boolean(resolvedMainQuestionText);
  const hasQuestionParameters = questionParameters.length > 0;
  const hasQuestionScoringFooter = Boolean(questionScoringFooter);
  const inferPromptLanguage = (promptText) => {
    const text = String(promptText || "").toLowerCase();
    if (!text.trim()) return null;

    const englishHints = [
      "how",
      "design",
      "response",
      "question",
      "score",
      "requires",
      "runtime",
      "idempotency",
    ];
    const frenchHints = [
      "comment",
      "question",
      "reponse",
      "réponse",
      "score",
      "maximal",
      "doit",
      "aborder",
    ];

    const countHints = (hints) =>
      hints.reduce(
        (count, token) => count + (new RegExp(`\\b${token}\\b`, "g").test(text) ? 1 : 0),
        0
      );

    const englishScore = countHints(englishHints);
    const frenchScore = countHints(frenchHints);

    if (englishScore > frenchScore) return "en";
    if (frenchScore > englishScore) return "fr";
    return null;
  };

  const openQuestionInputLang = (() => {
    // Prefer evaluation-level language (set from detail when user started the evaluation),
    // then question-level language from API, then infer from prompt, then browser.
    // This ensures "Single answer expected." / "Multiple answers expected." and other
    // section labels match the evaluation language (e.g. French) instead of being
    // overridden by a question-level or default "en".
    const browserLang = typeof navigator !== "undefined" ? navigator.language : "";
    const evaluationLang = localStorage.getItem(`sttLang_${uuid}`);
    const rawLang =
      evaluationLang || currentQuestion?.language || browserLang;
    const normalized = String(rawLang || "").toLowerCase();

    if (normalized.startsWith("en") || normalized.includes("angl")) return "en";
    if (normalized.startsWith("fr") || normalized.includes("fran")) return "fr";

    const inferredFromPrompt = inferPromptLanguage(
      resolvedMainQuestionText || questionTextFallback
    );
    return inferredFromPrompt || "en";
  })();

  // Calculate progress percentage
  const progressPercentage = (timeLeft / currentQuestion.responseTime) * 100;
  const questionMaxPoints = Number.isFinite(Number(currentQuestion?.maxPoints))
    ? Number(currentQuestion.maxPoints)
    : 10;
  const previewPoints = Number(previewResult?.points);
  const previewMaxPoints = Number(previewResult?.max_points);
  const previewCompleteness =
    Number.isFinite(previewPoints) &&
    Number.isFinite(previewMaxPoints) &&
    previewMaxPoints > 0
      ? Math.round((previewPoints / previewMaxPoints) * 100)
      : null;
  const currentSkillKey = getGroupKey(currentQuestion);
  const skillQuestions = questions
    .filter((q) => getGroupKey(q) === currentSkillKey)
    .sort((a, b) => a.questionNumber - b.questionNumber);
  const skillQuestionTotal = skillQuestions.length || 1;
  const skillQuestionIndex = skillQuestions.findIndex(
    (q) => q.questionId === currentQuestion.questionId
  ) + 1;
  const displayedSkillQuestionIndex = skillQuestionIndex > 0 ? skillQuestionIndex : 1;

  // Section labels follow test context language (same as openQuestionInputLang)
  const sectionLabels =
    SECTION_LABELS[openQuestionInputLang] || SECTION_LABELS.en;

  // Normalize question text so each newline behaves like a blank line (more spacing)
  // while preserving formatting inside fenced code blocks
  const normalizedQuestionText = normalizeQuestionText(questionTextFallback);
  const normalizedQuestionContext = normalizeQuestionText(questionContext);
  const normalizedMainQuestionText = normalizeQuestionText(resolvedMainQuestionText);
  const normalizedQuestionScoringFooter = normalizeQuestionText(questionScoringFooter);
  const questionMarkdownSx = {
    lineHeight: 1.6,
    "& p": { marginBottom: "0.75rem" },
    "& hr": {
      marginTop: "0.9rem",
      marginBottom: "1.5rem",
      borderColor: "gray.300",
    },
    "& strong": { fontWeight: 600 },
  };
  const questionMarkdownComponents = {
    p: ({ node, ...props }) => (
      <Text
        fontSize="lg"
        color="gray.800"
        lineHeight="1.6"
        fontWeight="medium"
        {...props}
      />
    ),
    ul: ({ node, ...props }) => (
      <Box as="ul" pl={5} mb={3} {...props} />
    ),
    ol: ({ node, ...props }) => (
      <Box as="ol" pl={5} mb={3} {...props} />
    ),
    li: ({ node, ...props }) => (
      <Box as="li" mb={1} {...props} />
    ),
    strong: ({ node, children, ...props }) => {
      const strongText = React.Children.toArray(children)
        .filter((child) => typeof child === "string" || typeof child === "number")
        .join("")
        .trim()
        .toLowerCase();
      const isScoringIndicator =
        strongText.includes("full score requires:") ||
        strongText.includes("score maximal requis:") ||
        strongText.includes("to achieve the maximum score, the response must address:") ||
        strongText.includes("pour obtenir le score maximal, la réponse doit aborder:");

      return (
        <Box
          as="strong"
          fontWeight={isScoringIndicator ? "bold" : "black"}
          color={isScoringIndicator ? "blue.900" : "purple.900"}
          bg={isScoringIndicator ? "blue.50" : "blue.100"}
          fontFamily={isScoringIndicator ? "heading" : "inherit"}
          letterSpacing={isScoringIndicator ? "0.01em" : "normal"}
          px={1.5}
          py={0.5}
          borderRadius="sm"
          {...props}
        >
          {children}
        </Box>
      );
    },
    code: ({ node, className, children, ...props }) => {
      const match = /language-(\w+)/.exec(className || "");
      const language = match ? match[1] : "";
      const content = String(children);
      // react-markdown v10+ no longer passes `inline`. Treat as block only when we have
      // a fenced block (language class) or multi-line content; single-backtick inline
      // code has no language and stays on one line, so render it inline for readability.
      const isBlock =
        (className && /language-\w+/.test(className)) || content.includes("\n");

      if (isBlock) {
        return (
          <Box
            borderWidth="1px"
            borderRadius="md"
            overflow="hidden"
            borderColor="gray.300"
            my={3}
          >
            <SyntaxHighlighter
              language={language || "text"}
              style={vscDarkPlus}
              showLineNumbers={true}
              customStyle={{
                margin: 0,
                borderRadius: "6px",
                fontSize: "14px",
                padding: "16px",
              }}
              wrapLines={true}
              lineNumberStyle={{
                minWidth: "3em",
                paddingRight: "1em",
                opacity: 0.5,
              }}
            >
              {content.replace(/\n$/, "")}
            </SyntaxHighlighter>
          </Box>
        );
      }

      return (
        <Box
          as="code"
          px={1}
          py={0.5}
          borderRadius="sm"
          bg="gray.100"
          fontFamily="mono"
          fontSize="sm"
          {...props}
        >
          {children}
        </Box>
      );
    },
  };
  const renderQuestionMarkdown = (text) => (
    <Box className="question-text" sx={questionMarkdownSx}>
      <ReactMarkdown
        remarkPlugins={[remarkBreaks]}
        components={questionMarkdownComponents}
      >
        {text}
      </ReactMarkdown>
    </Box>
  );

  return (
    <Box minH="100vh" bg="gray.50" py={3}>
      <Container maxW={{ base: "container.xl", lg: "1600px" }}>
        <Box bg="white" borderRadius="lg" borderWidth="1px" shadow="sm">
          {/* Header Section */}
          <Box p={4} borderBottomWidth="1px">
            <VStack spacing={3} align="stretch">
              <HStack justify="space-between" align="flex-start" flexWrap="wrap">
                <Heading size="lg" color="black">
                  Question {displayedSkillQuestionIndex}
                </Heading>
                <HStack spacing={4} flexWrap="wrap">
                  <Badge colorScheme="purple" fontSize="md" px={4} py={2} borderRadius="md">
                    {currentQuestion.technology}
                  </Badge>
                  {currentQuestion.questionType === 'MULTI-CHOICE' && (
                    <Badge colorScheme="orange" fontSize="sm" px={3} py={1}>
                      Réponses multiples
                    </Badge>
                  )}
                  <Badge colorScheme="blue" fontSize="sm" px={3} py={1}>
                    {currentQuestion.difficulty}
                  </Badge>
                  {currentQuestion.theme && (
                    <Badge colorScheme="green" fontSize="sm" px={3} py={1}>
                      {currentQuestion.theme}
                    </Badge>
                  )}
                  <Badge colorScheme="cyan" fontSize="sm" px={3} py={1}>
                    {questionMaxPoints} points
                  </Badge>
                  <Badge fontSize="sm" px={3} py={1}>
                    {displayedSkillQuestionIndex} / {skillQuestionTotal}
                  </Badge>
                  <HStack spacing={2} color="gray.700">
                    <TimeIcon />
                    <Text fontWeight="medium" fontSize="md" sx={{ marginBottom: "0 !important" }}>
                      {formatTime(timeLeft)} / {formatTime(currentQuestion.responseTime)}
                    </Text>
                    <Tooltip label={isSoundEnabled ? t("evaluation.soundOn") : t("evaluation.soundOff")} fontSize="sm" hasArrow>
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={() => setIsSoundEnabled((v) => !v)}
                        aria-label={isSoundEnabled ? t("evaluation.soundOn") : t("evaluation.soundOff")}
                      >
                        <Box as="i" className={`fa-solid ${isSoundEnabled ? "fa-volume-high" : "fa-volume-xmark"}`} />
                      </Button>
                    </Tooltip>
                  </HStack>
                </HStack>
              </HStack>

              <Box position="relative">
                <Progress
                  value={progressPercentage}
                  size="md"
                  colorScheme={progressPercentage > 20 ? "purple" : "red"}
                  borderRadius="full"
                  sx={progressPercentage <= 20 ? {
                    "& > div": {
                      animation: "pulse-bar 1s ease-in-out infinite",
                    },
                    "@keyframes pulse-bar": {
                      "0%, 100%": { opacity: 1 },
                      "50%": { opacity: 0.5 },
                    },
                  } : undefined}
                />
                {progressPercentage <= 20 && (
                  <HStack spacing={1} mt={1} justify="center">
                    <WarningIcon color="red.500" boxSize="14px" />
                    <Text fontSize="sm" color="red.600" fontWeight="semibold" sx={{ marginBottom: "0 !important" }}>
                      {t("evaluation.timeRunningLow")}
                    </Text>
                  </HStack>
                )}
              </Box>

              <HStack spacing={1} justify="center" color="gray.600" align="center">
                <WarningIcon boxSize="12px" />
                <Text fontSize="xs" sx={{ marginBottom: "0 !important" }}>Copier-coller désactivé</Text>
              </HStack>
            </VStack>
          </Box>

          {/* Question + Answer: split-screen on desktop, stacked on mobile */}
          <Grid
            templateColumns={isDesktop ? "1fr 1fr" : "1fr"}
            gap={0}
          >
          {/* Question Content */}
          <GridItem
            p={4}
            bg="gray.50"
            borderBottomWidth={isDesktop ? "0" : "1px"}
            borderRightWidth={isDesktop ? "1px" : "0"}
            overflowY={isDesktop ? "auto" : "visible"}
            maxH={isDesktop ? "calc(100vh - 200px)" : "none"}
          >
            <VStack spacing={3} align="stretch">
              {isStructuredQuestionType ? (
                <Heading size="md" sx={{ marginBottom: "0 !important" }}>
                  {questionTitle || sectionLabels.question}
                </Heading>
              ) : (
                <HStack spacing={2}>
                  <InfoIcon color="purple.500" />
                  <Heading size="md" sx={{ marginBottom: "0 !important" }}>{sectionLabels.question}</Heading>
                </HStack>
              )}

              {isStructuredQuestionType ? (
                <VStack spacing={3} align="stretch">
                  {hasQuestionContext && (
                    <Box
                      bg="white"
                      borderWidth="1px"
                      borderColor="gray.200"
                      borderRadius="md"
                      p={3}
                    >
                      <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        color="gray.600"
                        textTransform="uppercase"
                        letterSpacing="0.04em"
                      >
                        {sectionLabels.context}
                      </Text>
                      <Box mt={2}>{renderQuestionMarkdown(normalizedQuestionContext)}</Box>
                    </Box>
                  )}

                  {hasQuestionMain && (
                    <Box
                      bg="white"
                      borderWidth="1px"
                      borderColor="gray.200"
                      borderRadius="md"
                      p={3}
                    >
                      <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        color="gray.600"
                        textTransform="uppercase"
                        letterSpacing="0.04em"
                      >
                        {sectionLabels.mainQuestion}
                      </Text>
                      <Box mt={2}>{renderQuestionMarkdown(normalizedMainQuestionText)}</Box>
                    </Box>
                  )}

                  {hasQuestionParameters && (
                    <Box
                      bg="white"
                      borderWidth="1px"
                      borderColor="gray.200"
                      borderRadius="md"
                      p={3}
                    >
                      <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        color="gray.600"
                        textTransform="uppercase"
                        letterSpacing="0.04em"
                      >
                        {sectionLabels.parameters}
                      </Text>
                      <Box as="ul" mt={2} pl={5}>
                        {questionParameters.map((parameter, index) => (
                          <Box as="li" mb={1} key={`${index}-${parameter}`}>
                            <Text color="gray.800" lineHeight="1.6">
                              {parameter}
                            </Text>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {hasQuestionScoringFooter && (
                    <Box
                      bg="blue.50"
                      borderWidth="1px"
                      borderColor="blue.200"
                      borderRadius="md"
                      p={3}
                    >
                      <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        color="blue.700"
                        textTransform="uppercase"
                        letterSpacing="0.04em"
                      >
                        {sectionLabels.scoringRules}
                      </Text>
                      <Box mt={2}>{renderQuestionMarkdown(normalizedQuestionScoringFooter)}</Box>
                    </Box>
                  )}
                </VStack>
              ) : (
                <>
                  {renderQuestionMarkdown(normalizedQuestionText)}
                  {(currentQuestion.questionType === "QCM" ||
                    currentQuestion.questionType === "MULTI-CHOICE") && (
                    <Box as="p" mt={2} mb={0} fontSize="lg" lineHeight="1.6">
                      <Box
                        as="span"
                        display="inline"
                        fontWeight="normal"
                        color="purple.900"
                        bg="blue.100"
                        fontFamily="inherit"
                        letterSpacing="normal"
                        px={1.5}
                        py={0.5}
                        borderRadius="sm"
                      >
                        {currentQuestion.questionType === "MULTI-CHOICE"
                          ? sectionLabels.multipleAnswersExpected
                          : sectionLabels.singleAnswerExpected}
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </VStack>
          </GridItem>

          {/* Answer Section */}
          <GridItem
            p={4}
            position={isDesktop ? "sticky" : "static"}
            top={isDesktop ? "0" : "auto"}
            alignSelf={isDesktop ? "stretch" : "auto"}
            maxH={isDesktop ? "calc(100vh - 200px)" : "none"}
            overflowY={isDesktop ? "auto" : "visible"}
            display="flex"
            flexDirection="column"
          >
            <VStack spacing={3} align="stretch" flex={isDesktop ? 1 : undefined}>
              <HStack spacing={2}>
                <CheckCircleIcon color="green.500" />
                <Heading size="md" sx={{ marginBottom: "0 !important" }}>Réponse</Heading>
              </HStack>
              
              {isOpenQuestionType(currentQuestion.questionType) ? (
                <VStack spacing={2} align="stretch" flex={isDesktop ? 1 : undefined}>
                  <Textarea
                    value={displayedAnswer}
                    onChange={handleTextChange}
                    spellCheck={true}
                    lang={openQuestionInputLang}
                    autoCapitalize="sentences"
                    autoCorrect="on"
                    onPaste={(e) => e.preventDefault()}
                    onCopy={(e) => e.preventDefault()}
                    onCut={(e) => e.preventDefault()}
                    onKeyDown={(e) => {
                      if ((e.ctrlKey || e.metaKey) &&
                          (e.key === 'c' || e.key === 'v' || e.key === 'x' || e.key === 'a')) {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                    placeholder="Entrez votre réponse ici..."
                    rows={4}
                    resize="vertical"
                    flex={isDesktop ? 1 : undefined}
                    minH={isDesktop ? "200px" : undefined}
                    bg="white"
                    borderColor="gray.300"
                    _focus={{ borderColor: "purple.500", boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)" }}
                  />
                  <HStack spacing={2}>
                    <Button
                      size="md"
                      bg={isRecording ? "red.500" : "rgb(37, 87, 167)"}
                      color="white"
                      _hover={{ bg: isRecording ? "red.600" : "rgb(27, 67, 137)" }}
                      _active={{ bg: isRecording ? "red.700" : "rgb(17, 47, 107)" }}
                      leftIcon={
                        isConnecting ? (
                          <Spinner size="sm" />
                        ) : (
                          <Box as="i" className={`fa-solid ${isRecording ? 'fa-stop' : 'fa-microphone'}`} />
                        )
                      }
                      onClick={() => {
                        if (isRecording) {
                          const t = (interimTranscript || '').trim();
                          if (t) handleSttFinal(t, sttQuestionRef.current ?? currentQuestion?.questionNumber);
                          stopSTT();
                        } else {
                          // Ensure we have the latest textAnswer state before starting STT
                          const currentText = textAnswer || "";
                          sttBaseRef.current = currentText;
                          setSttBaseText(currentText);
                          setSttCommitted([]);
                          lastSttFinalRef.current = "";
                          // Bind this STT session to the current question number
                          sttQuestionRef.current = currentQuestion?.questionNumber || null;
                          
                          // Increment session counter for debugging
                          sttSessionRef.current += 1;
                          const currentSession = sttSessionRef.current;
                          
                          // Debug logging for STT session start
                          if (process.env.NODE_ENV === 'development') {
                            console.log('STT Session started:', {
                              sessionId: currentSession,
                              baseText: currentText,
                              questionNumber: currentQuestion?.questionNumber,
                              timestamp: new Date().toISOString()
                            });
                          }
                          
                          const savedLang = localStorage.getItem(`sttLang_${uuid}`) || 'fr-FR';
                          const sessionQuestionNumber = currentQuestion?.questionNumber || null;
                          sttQuestionRef.current = sessionQuestionNumber;
                          startSTT(
                            { language: savedLang },
                            (text) => handleSttFinal(text, sessionQuestionNumber)
                          );
                        }
                      }}
                      isDisabled={timeLeft <= 0 || isConnecting}
                    >
                      {isConnecting ? 'Initialisation…' : (isRecording ? 'Arrêter la dictée' : 'Démarrer la dictée')}
                    </Button>
                    <Tooltip
                      label={sectionLabels.micHint}
                      fontSize="sm"
                      placement="top"
                      hasArrow
                      maxW="300px"
                    >
                      <Box as="span" display="inline-flex" alignItems="center" cursor="help">
                        <QuestionOutlineIcon color="gray.500" boxSize={4} />
                      </Box>
                    </Tooltip>
                    {sttError && (
                      <Text color="red.500" fontSize="sm">
                        Dictée indisponible: {sttError}
                      </Text>
                    )}
                  </HStack>
                </VStack>
              ) : isCodingQuestionType(currentQuestion.questionType) ? (
                <VStack spacing={3} align="stretch" flex={isDesktop ? 1 : undefined}>
                  <Box borderWidth="1px" borderRadius="md" overflow="hidden" flex={isDesktop ? 1 : undefined} minH={isDesktop ? "300px" : undefined} display="flex" flexDirection="column">
                    {(() => {
                      const language = currentQuestion.codingLanguage || 'javascript';
                      return (
                        <CodeEditor
                          language={language}
                          languageVersion={currentQuestion.codingLanguageVersion || null}
                          value={textAnswer}
                          onChange={handleTextChange}
                          autoCompleteWords={currentQuestion.autoCompleteWords}
                          evaluationId={uuid}
                          height={isDesktop ? "100%" : "400px"}
                        />
                      );
                    })()}
                  </Box>

                  <HStack spacing={3}>
                    <Button
                      variant="outline"
                      colorScheme="blue"
                      onClick={handlePreviewScoring}
                      isLoading={previewLoading}
                      loadingText="Testing..."
                    >
                      Test answer
                    </Button>
                    {previewCompleteness !== null && (
                      <Text fontWeight="semibold" color="blue.700" sx={{ marginBottom: "0 !important" }}>
                        Completeness: {previewCompleteness}%
                      </Text>
                    )}
                  </HStack>

                  {previewError && (
                    <Alert status="error" borderRadius="md">
                      <AlertIcon />
                      <Text fontSize="sm" sx={{ marginBottom: "0 !important" }}>{previewError}</Text>
                    </Alert>
                  )}
                </VStack>
              ) : currentQuestion.questionType === 'CODE_COMPLETION' ? (
                <VStack spacing={3} align="stretch" flex={isDesktop ? 1 : undefined}>
                  <Text fontSize="sm" color="gray.600" fontWeight="medium">
                    Entrez votre réponse (ce qui remplace XXX):
                  </Text>
                  <Textarea
                    value={textAnswer}
                    onChange={handleTextChange}
                    onPaste={(e) => e.preventDefault()}
                    onCopy={(e) => e.preventDefault()}
                    onCut={(e) => e.preventDefault()}
                    onKeyDown={(e) => {
                      if ((e.ctrlKey || e.metaKey) &&
                          (e.key === 'c' || e.key === 'v' || e.key === 'x' || e.key === 'a')) {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    placeholder="Tapez votre réponse ici..."
                    bg="white"
                    borderColor="gray.300"
                    _focus={{ borderColor: "purple.500", boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)" }}
                    fontFamily="monospace"
                    fontSize="md"
                    flex={isDesktop ? 1 : undefined}
                    minH={isDesktop ? "320px" : "160px"}
                    resize="vertical"
                  />
                  <Text fontSize="xs" color="gray.500">
                    💡 Conseil: Tapez uniquement l'élément manquant, sans code supplémentaire
                  </Text>
                </VStack>
              ) : (
                <Stack spacing={2} flex={isDesktop ? 1 : undefined}>
                  {options.map((option, index) => (
                    <Box
                      key={option.id}
                      borderWidth="1px"
                      borderRadius="md"
                      p={3}
                      bg={selectedOptions.includes(option.id) ? "purple.50" : "white"}
                      borderColor={selectedOptions.includes(option.id) ? "purple.500" : "gray.200"}
                      cursor="pointer"
                      transition="all 0.2s"
                      _hover={{ bg: selectedOptions.includes(option.id) ? "purple.100" : "gray.50" }}
                      onClick={() => handleOptionToggle(option.id)}
                    >
                      <HStack spacing={2}>
                        {currentQuestion.questionType === 'MULTI-CHOICE' ? (
                          <Checkbox
                            isChecked={selectedOptions.includes(option.id)}
                            onChange={() => handleOptionToggle(option.id)}
                            colorScheme="purple"
                            size="lg"
                          />
                        ) : (
                          <Radio
                            isChecked={selectedOptions.includes(option.id)}
                            onChange={() => handleOptionToggle(option.id)}
                            colorScheme="purple"
                            size="lg"
                          />
                        )}
                        <Text fontWeight="medium" flex="1" fontSize="md" sx={{ marginBottom: "0 !important" }}>
                          Option {index + 1}: {option.text}
                        </Text>
                      </HStack>
                    </Box>
                  ))}
                </Stack>
              )}
            </VStack>
          </GridItem>
          </Grid>

          {/* Footer Section */}
          <Box p={4} borderTopWidth="1px" bg="gray.50">
            <Flex justify="space-between" align="center">
              <Button
                variant="outline"
                colorScheme="gray"
                onClick={handleReportQuestion}
                leftIcon={<WarningIcon />}
              >
                Signaler la question
              </Button>
              {isLastQuestion ? (
                <Button
                  bg="rgb(37, 87, 167)"
                  color="white"
                  _hover={{ bg: "rgb(27, 67, 137)" }}
                  _active={{ bg: "rgb(17, 47, 107)" }}
                  size="lg"
                  onClick={handleSubmit}
                  rightIcon={<CheckCircleIcon />}
                >
                  Soumettre
                </Button>
              ) : (
                <Button
                  bg="rgb(37, 87, 167)"
                  color="white"
                  _hover={{ bg: "rgb(27, 67, 137)" }}
                  _active={{ bg: "rgb(17, 47, 107)" }}
                  size="lg"
                  onClick={handleNextQuestion}
                  rightIcon={<ChevronRightIcon />}
                >
                  Suivant
                </Button>
              )}
            </Flex>
          </Box>
        </Box>
      </Container>

      <ReportQuestionModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        onSubmit={submitReportReason}
      />
    </Box>
  );
};

export default QuestionPage; 
